import { useField, useFormikContext } from 'formik';
import type { FieldMetaProps } from 'formik';

import { AsyncComboBox, AsyncMultiComboBox, ComboBox, MultiComboBox } from './ComboBox';
import type {
    AsyncComboBoxProps,
    AsyncMultiComboBoxProps,
    ComboBoxOption,
    ComboBoxProps,
    ComboBoxValue,
    MultiComboBoxProps,
    MultiComboBoxValue,
} from './ComboBox';

type FormikComboBoxProps<Option extends ComboBoxOption> = Omit<
    ComboBoxProps<Option>,
    'value' | 'onChange' | 'negative' | 'onBlur'
> & {
    name: string;
};

type FormikAsyncComboBoxProps<Option extends ComboBoxOption> = Omit<
    AsyncComboBoxProps<Option>,
    'value' | 'onChange' | 'negative' | 'onBlur'
> & {
    name: string;
};

type FormikMultiComboBoxProps<Option extends ComboBoxOption> = Omit<
    MultiComboBoxProps<Option>,
    'value' | 'onChange' | 'negative' | 'onBlur'
> & {
    name: string;
};

type FormikAsyncMultiComboBoxProps<Option extends ComboBoxOption> = Omit<
    AsyncMultiComboBoxProps<Option>,
    'value' | 'onChange' | 'negative' | 'onBlur'
> & {
    name: string;
};

const getCaptionValue = (
    meta: FieldMetaProps<unknown>,
    caption?: ComboBoxProps['caption']
): ComboBoxProps['caption'] => {
    return meta.touched && meta.error && typeof meta.error === 'string' ? meta.error : caption;
};

export function FormikComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    name,
    caption,
    ...rest
}: FormikComboBoxProps<Option>) {
    const [field, meta, helpers] = useField<ComboBoxValue<Option>>(name);
    const { validateOnChange } = useFormikContext();
    const captionValue = getCaptionValue(meta, caption);

    const handleChange = (nextValue: ComboBoxValue<Option>) => {
        helpers.setValue(nextValue);

        if (!validateOnChange && meta.error) {
            helpers.setError(undefined);
        }
    };

    return (
        <ComboBox<Option>
            {...rest}
            value={field.value}
            onChange={handleChange}
            negative={meta.touched && !!meta.error}
            caption={captionValue}
            onBlur={() => {
                helpers.setTouched(true);
            }}
        />
    );
}

export function FormikAsyncComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    name,
    caption,
    ...rest
}: FormikAsyncComboBoxProps<Option>) {
    const [field, meta, helpers] = useField<ComboBoxValue<Option>>(name);
    const { validateOnChange } = useFormikContext();
    const captionValue = getCaptionValue(meta, caption);

    const handleChange = (nextValue: ComboBoxValue<Option>) => {
        helpers.setValue(nextValue);

        if (!validateOnChange && meta.error) {
            helpers.setError(undefined);
        }
    };

    return (
        <AsyncComboBox<Option>
            {...rest}
            value={field.value}
            onChange={handleChange}
            negative={meta.touched && !!meta.error}
            caption={captionValue}
            onBlur={() => {
                helpers.setTouched(true);
            }}
        />
    );
}

export function FormikMultiComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    name,
    caption,
    ...rest
}: FormikMultiComboBoxProps<Option>) {
    const [field, meta, helpers] = useField<MultiComboBoxValue<Option>>(name);
    const { validateOnChange } = useFormikContext();
    const captionValue = getCaptionValue(meta, caption);

    const handleChange = (nextValue: MultiComboBoxValue<Option>) => {
        helpers.setValue(nextValue);

        if (!validateOnChange && meta.error) {
            helpers.setError(undefined);
        }
    };

    return (
        <MultiComboBox<Option>
            {...rest}
            value={field.value}
            onChange={handleChange}
            negative={meta.touched && !!meta.error}
            caption={captionValue}
            onBlur={() => {
                helpers.setTouched(true);
            }}
        />
    );
}

export function FormikAsyncMultiComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    name,
    caption,
    ...rest
}: FormikAsyncMultiComboBoxProps<Option>) {
    const [field, meta, helpers] = useField<MultiComboBoxValue<Option>>(name);
    const { validateOnChange } = useFormikContext();
    const captionValue = getCaptionValue(meta, caption);

    const handleChange = (nextValue: MultiComboBoxValue<Option>) => {
        helpers.setValue(nextValue);

        if (!validateOnChange && meta.error) {
            helpers.setError(undefined);
        }
    };

    return (
        <AsyncMultiComboBox<Option>
            {...rest}
            value={field.value}
            onChange={handleChange}
            negative={meta.touched && !!meta.error}
            caption={captionValue}
            onBlur={() => {
                helpers.setTouched(true);
            }}
        />
    );
}
